import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from 'react-dom';
import { FaSyncAlt } from "react-icons/fa";
import { Button } from "../Button";
import {
  Promotion,
  PromotionFormValues,
  PROMOTION_STATUS,
} from "../../types/promotion.type";

interface PromotionFormModalProps {
  open: boolean;
  loading?: boolean;
  error?: string;
  editingPromotion?: Promotion | null;
  onClose: () => void;
  onConfirm: (values: PromotionFormValues) => Promise<void> | void;
}

// Generates a short, URL-safe random tracking ID for QR campaigns.
const generateTrackingId = (): string => {
  const part = () => Math.random().toString(36).slice(2, 8);
  return `qr_${part()}${part()}`.toLowerCase();
};

const defaultValues: PromotionFormValues = {
  mode: "qr",
  campaignName: "",
  trackingId: generateTrackingId(),
  totalDonations: 0,
  donationsPerUser: 0,
  foodBarsPerDonation: 0,
  isMetered: true,
};

const PromotionFormModal: React.FC<PromotionFormModalProps> = ({
  open,
  loading = false,
  error,
  editingPromotion,
  onClose,
  onConfirm,
}) => {
  const [values, setValues] = useState<PromotionFormValues>(defaultValues);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const isEditing = Boolean(editingPromotion);

  useEffect(() => {
    if (!open) return;
    setValidationErrors({});
    if (editingPromotion) {
      // Infer mode from existing data: redeem codes are typically short, all-caps, non-metered.
      const inferredMode =
        editingPromotion.isMetered || editingPromotion.trackingId?.startsWith("qr_")
          ? "qr"
          : "redeem";
      setValues({
        mode: inferredMode,
        campaignName: editingPromotion.campaignName || "",
        trackingId: editingPromotion.trackingId || "",
        totalDonations: Number(editingPromotion.totalDonations || 0),
        donationsPerUser: Number(editingPromotion.donationsPerUser || 0),
        foodBarsPerDonation: Number(editingPromotion.foodBarsPerDonation || 0),
        isMetered: Boolean(editingPromotion.isMetered),
      });
    } else {
      setValues({ ...defaultValues, trackingId: generateTrackingId() });
    }
  }, [open, editingPromotion]);

  const trackingIdLabel = useMemo(
    () => (values.mode === "qr" ? "Tracking ID" : "Redeem Code"),
    [values.mode],
  );

  const trackingIdPlaceholder = useMemo(
    () => (values.mode === "qr" ? "Auto-generated" : "e.g. SUMMER25"),
    [values.mode],
  );

  if (!open) return null;

  const setField = <K extends keyof PromotionFormValues>(
    key: K,
    value: PromotionFormValues[K],
  ) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setValidationErrors((prev) => {
      if (!prev[key as string]) return prev;
      const { [key as string]: _omit, ...rest } = prev;
      return rest;
    });
  };

  const setMode = (mode: "qr" | "redeem") => {
    setValues((prev) => ({
      ...prev,
      mode,
      // Auto-suggest a fresh ID when switching to QR; clear when switching to Redeem.
      trackingId:
        mode === "qr"
          ? prev.trackingId && prev.trackingId.startsWith("qr_")
            ? prev.trackingId
            : generateTrackingId()
          : "",
      // Redeem codes are not metered (matches reference UI behavior).
      isMetered: mode === "qr" ? prev.isMetered : false,
    }));
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};

    if (!values.campaignName.trim()) {
      next.campaignName = "Promotion name is required";
    }

    const trackingId = values.trackingId.trim();
    if (!trackingId) {
      next.trackingId =
        values.mode === "qr" ? "Tracking ID is required" : "Redeem code is required";
    } else if (!/^[A-Za-z0-9_\-]+$/.test(trackingId)) {
      next.trackingId = "Only letters, numbers, hyphens, and underscores are allowed";
    } else if (trackingId.length < 3 || trackingId.length > 64) {
      next.trackingId = "Must be 3-64 characters";
    }

    if (!Number.isFinite(values.totalDonations) || values.totalDonations < 0) {
      next.totalDonations = "Must be 0 or greater";
    }
    if (!Number.isFinite(values.donationsPerUser) || values.donationsPerUser < 0) {
      next.donationsPerUser = "Must be 0 or greater";
    }
    if (
      !Number.isFinite(values.foodBarsPerDonation) ||
      values.foodBarsPerDonation < 0
    ) {
      next.foodBarsPerDonation = "Must be 0 or greater";
    }

    setValidationErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleConfirm = async () => {
    if (!validate()) return;
    await onConfirm({
      ...values,
      campaignName: values.campaignName.trim(),
      trackingId: values.trackingId.trim(),
      totalDonations: Number(values.totalDonations || 0),
      donationsPerUser: Number(values.donationsPerUser || 0),
      foodBarsPerDonation: Number(values.foodBarsPerDonation || 0),
    });
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4"
      onClick={() => {
        if (!loading) onClose();
      }}
    >
      <div
        className="w-full max-w-3xl rounded-2xl border border-white/50 bg-white p-6 shadow-xl dark:border-white/10 dark:bg-slate-900"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          {isEditing ? "Edit Promotion" : "Create Promotion"}
        </h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          {isEditing
            ? "Update the promotion details below."
            : "Generate a scannable QR campaign or a typed redeem code."}
        </p>

        {/* Mode switch */}
        <div className="mt-5 flex items-center gap-6">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-800 dark:text-gray-200">
            <input
              type="radio"
              name="promotion-mode"
              checked={values.mode === "qr"}
              onChange={() => setMode("qr")}
              disabled={isEditing}
              className="h-4 w-4 text-primary-100 focus:ring-primary-100"
            />
            QR Campaign
          </label>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-800 dark:text-gray-200">
            <input
              type="radio"
              name="promotion-mode"
              checked={values.mode === "redeem"}
              onChange={() => setMode("redeem")}
              disabled={isEditing}
              className="h-4 w-4 text-primary-100 focus:ring-primary-100"
            />
            Redeem Code
          </label>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Left column */}
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Promotion Name*
              </label>
              <input
                type="text"
                value={values.campaignName}
                onChange={(event) => setField("campaignName", event.target.value)}
                className="w-full rounded-md border border-gray-300 p-2 focus:border-primary-100 focus:ring-primary-100 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
                placeholder="e.g. Summer 2026 Drive"
              />
              {validationErrors.campaignName && (
                <p className="mt-1 text-sm text-red-500">{validationErrors.campaignName}</p>
              )}
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {trackingIdLabel}*
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={values.trackingId}
                  readOnly={values.mode === "qr"}
                  onChange={(event) =>
                    setField("trackingId", event.target.value.trim())
                  }
                  className="w-full rounded-md border border-gray-300 p-2 focus:border-primary-100 focus:ring-primary-100 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
                  placeholder={trackingIdPlaceholder}
                />
                {values.mode === "qr" && !isEditing && (
                  <button
                    type="button"
                    onClick={() => setField("trackingId", generateTrackingId())}
                    className="rounded-md border border-gray-300 p-2 text-primary-100 hover:bg-primary-50 dark:border-white/10 dark:hover:bg-white/10"
                    title="Regenerate Tracking ID"
                    aria-label="Regenerate Tracking ID"
                  >
                    <FaSyncAlt />
                  </button>
                )}
              </div>
              {validationErrors.trackingId && (
                <p className="mt-1 text-sm text-red-500">{validationErrors.trackingId}</p>
              )}
              {isEditing && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Tracking ID cannot be changed once created without affecting existing
                  QR codes in circulation.
                </p>
              )}
            </div>

            <label className="flex items-center gap-2 text-sm font-medium text-gray-800 dark:text-gray-200">
              <input
                type="checkbox"
                checked={values.isMetered}
                disabled={values.mode === "redeem"}
                onChange={(event) => setField("isMetered", event.target.checked)}
                className="h-4 w-4 rounded text-primary-100 focus:ring-primary-100"
              />
              Enable Metered Promotion
            </label>
          </div>

          {/* Right column */}
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Total Allowed Donations
              </label>
              <input
                type="number"
                min={0}
                step={1}
                value={values.totalDonations}
                onChange={(event) =>
                  setField("totalDonations", Number(event.target.value || 0))
                }
                className="w-full rounded-md border border-gray-300 p-2 focus:border-primary-100 focus:ring-primary-100 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
              />
              {validationErrors.totalDonations && (
                <p className="mt-1 text-sm text-red-500">{validationErrors.totalDonations}</p>
              )}
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Total Allowed Donations Per User
              </label>
              <input
                type="number"
                min={0}
                step={1}
                value={values.donationsPerUser}
                onChange={(event) =>
                  setField("donationsPerUser", Number(event.target.value || 0))
                }
                className="w-full rounded-md border border-gray-300 p-2 focus:border-primary-100 focus:ring-primary-100 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
              />
              {validationErrors.donationsPerUser && (
                <p className="mt-1 text-sm text-red-500">{validationErrors.donationsPerUser}</p>
              )}
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                No. of Free Food Bars Per Donation
              </label>
              <input
                type="number"
                min={0}
                step={1}
                value={values.foodBarsPerDonation}
                onChange={(event) =>
                  setField("foodBarsPerDonation", Number(event.target.value || 0))
                }
                className="w-full rounded-md border border-gray-300 p-2 focus:border-primary-100 focus:ring-primary-100 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
              />
              {validationErrors.foodBarsPerDonation && (
                <p className="mt-1 text-sm text-red-500">
                  {validationErrors.foodBarsPerDonation}
                </p>
              )}
            </div>
          </div>
        </div>

        {error && (
          <p className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300">
            {error}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirm} loading={loading}>
            {isEditing ? "Save Changes" : "Create Promotion"}
          </Button>
        </div>

        {/* Tiny visual hint about status for editing */}
        {isEditing && editingPromotion?.status === PROMOTION_STATUS.DEACTIVATED && (
          <p className="mt-3 text-xs text-amber-600 dark:text-amber-300">
            This promotion is currently deactivated.
          </p>
        )}
      </div>
    </div>
  ,
  document.body
);
};

export default PromotionFormModal;
